import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { chipStyles, typographyStyles } from '../../../modals/unified_config_modals/styles/unifiedConfigStyles';

export interface PillOption<TValue extends string | number> {
  value: TValue;
  labelKey: string;
}

interface Props<TValue extends string | number> {
  label: string;
  options: PillOption<TValue>[];
  selectedValue: TValue;
  onSelect: (value: TValue) => void;
  /**
   * Optional grey caption rendered under the pill row. Used for the
   * "Applies to both Voltage and Current" hint on the shared Auto-Adapt row.
   */
  captionKey?: string;
}

function PillRowSelectorInner<TValue extends string | number>({
  label,
  options,
  selectedValue,
  onSelect,
  captionKey,
}: Props<TValue>) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={typographyStyles.subsectionHeading}>
        {label}
      </Typography>

      <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 2 }}>
        {options.map((opt) => (
          <Box
            key={String(opt.value)}
            onClick={() => onSelect(opt.value)}
            sx={chipStyles.alertOptionChip(selectedValue === opt.value)}
          >
            {opt.labelKey}
          </Box>
        ))}
      </Stack>

      {captionKey && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
        >
          {captionKey}
        </Typography>
      )}
    </Box>
  );
}

const PillRowSelector = PillRowSelectorInner as <TValue extends string | number>(
  props: Props<TValue>,
) => React.ReactElement;

export default PillRowSelector;
